import {
  createSelfEvaluationReview,
  fetchSelfEvaluationReview,
  updatePerformanceReview,
  // updateSelfEvaluationReview,
} from "@/modules/performance/feedbacks/api";

import { createPerformanceFormSchema } from "./schema";
import { fetchPerformanceReview } from "@/modules/performance/components/api";
import { sections } from "@/modules/performance/feedbacks/feedback/constants";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSelector } from "react-redux";
import { selectUser } from "store/src/slices/auth-slice";

function buildDefaultValues(formSections = sections) {
  const values = {};
  formSections.forEach((sec) => {
    sec.items.forEach((it) => {
      if (it.type === "textarea") {
        values[it.key] = "";
      } else {
        // Default rating is 0 (not observed)
        values[it.key] = 0;
      }
    });
  });
  return values;
}

export const usePerformanceForm = ({
  selfEvaluation,
  sections: formSections,
  performanceId,
}) => {
  const { id: employeeId } = useSelector(selectUser);

  const defaultValues = useMemo(
    () => buildDefaultValues(formSections),
    [formSections],
  );
  const { quadId } = useParams();

  const schema = useMemo(() => {
    if (formSections) {
      return createPerformanceFormSchema(formSections);
    }
    return createPerformanceFormSchema(sections);
  }, [formSections]);

  const form = useForm({
    defaultValues,
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  async function handleCreate(context) {
    try {
      const transformedValues = { ...context.values, status: "submitted" };
      const transformedDirtyValues = {
        ...context.dirtyValues,
        status: "submitted",
      };

      if (selfEvaluation) {
        const existingReview = await fetchSelfEvaluationReview({ quadId });
        if (existingReview) {
          const response = await updatePerformanceReview({
            id: existingReview.id,
            payload: transformedDirtyValues,
          });
          toast.success("Self-evaluation updated successfully");
          return response;
        } else {
          const response = await createSelfEvaluationReview({
            quadId,
            payload: transformedValues,
            employeeId: employeeId,
          });
          toast.success("Self-evaluation created successfully");
          return response;
        }
      } else {
        const response = await updatePerformanceReview({
          id: context.itemId,
          payload: transformedValues,
        });
        toast.success("Performance review updated successfully");
        return response;
      }
    } catch (error) {
      console.error("Error creating/updating performance review:", error);
      toast.error(error.message || "Failed to save performance review");
      throw error;
    }
  }

  async function handleRead(id) {
    try {
      let existingReview = null;

      if (selfEvaluation) {
        existingReview = await fetchSelfEvaluationReview({ quadId });
      } else {
        existingReview = await fetchPerformanceReview({ reviewId: id });
      }

      if (existingReview) {
        return { values: existingReview };
      }

      return { values: defaultValues };
    } catch (error) {
      console.error("Error reading performance review:", error);
      toast.error(error.message || "Failed to load performance review");
      return { values: defaultValues };
    }
  }

  async function handleUpdate(id, context) {
    try {
      const transformedValues = { ...context.values, status: "submitted" };

      const transformedDirtyValues = {
        ...context.dirtyValues,
        status: "submitted",
      };

      if (selfEvaluation) {
        const existingReview = await fetchSelfEvaluationReview({ quadId });
        if (existingReview) {
          const response = await updatePerformanceReview({
            id: existingReview.id,
            payload: transformedDirtyValues,
          });
          toast.success("Self-evaluation updated successfully");
          return response;
        } else {
          const response = await createSelfEvaluationReview({
            quadId,
            payload: transformedValues,
            employeeId: employeeId,
          });
          toast.success("Self-evaluation created successfully");
          return response;
        }
      } else {
        const response = await updatePerformanceReview({
          id,
          payload: transformedValues,
        });
        toast.success("Performance review updated successfully");
        return response;
      }
    } catch (error) {
      console.error("Error updating performance review:", error);
      toast.error(error.message || "Failed to update performance review");
      throw error;
    }
  }

  return {
    form,
    handleCreate,
    handleRead,
    handleUpdate,
  };
};
